import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { Slider } from "@/components/ui/slider";
import { Sparkles } from "lucide-react";

const DATA = [
  { year: "2019", Gold: 100, Nifty: 100, FD: 100 },
  { year: "2020", Gold: 128, Nifty: 96, FD: 106 },
  { year: "2021", Gold: 135, Nifty: 128, FD: 112 },
  { year: "2022", Gold: 148, Nifty: 134, FD: 119 },
  { year: "2023", Gold: 171, Nifty: 152, FD: 126 },
  { year: "2024", Gold: 205, Nifty: 168, FD: 133 },
  { year: "2025", Gold: 242, Nifty: 179, FD: 141 },
];

const ALLOCATION = [
  { label: "Digital Gold", pct: 62, color: "#D4AF37" },
  { label: "Nifty 50 ETF", pct: 26, color: "#5C544E" },
  { label: "Fixed Deposit", pct: 12, color: "#E8C4A0" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="rounded-xl border border-amber-200/60 bg-white/95 p-3 text-xs shadow-lg backdrop-blur">
      <p className="mb-1 font-semibold text-charcoal">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: ₹{p.value}
        </p>
      ))}
    </div>
  );
};

export const Dashboard = () => {
  const [monthly, setMonthly] = useState([3000]);
  const projected = Math.round(monthly[0] * 12 * 8 * 1.62);

  return (
    <section id="portfolio-dashboard" data-testid="portfolio-dashboard" className="relative bg-ivory py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          overline="Portfolio dashboard"
          title="See your gold outshine the rest"
          accentWord="outshine"
          subtitle="Track gold against the Nifty 50 and fixed deposits, watch your allocation, and simulate what tomorrow could look like."
        />

        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {/* Chart */}
          <Reveal className="lg:col-span-2">
            <div className="glass-card rounded-3xl p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl font-normal text-charcoal">Growth of ₹100</h3>
                  <p className="text-sm text-charcoal-soft">Gold vs Nifty 50 vs FD · 2019–2025</p>
                </div>
                <div className="flex gap-4 text-xs font-medium">
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-gold" />Gold</span>
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-charcoal-soft" />Nifty 50</span>
                  <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-gold-rose" />FD</span>
                </div>
              </div>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={DATA} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8DFD0" vertical={false} />
                    <XAxis dataKey="year" stroke="#5C544E" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#5C544E" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="FD" stroke="#E8C4A0" strokeWidth={2} fill="none" />
                    <Area type="monotone" dataKey="Nifty" stroke="#5C544E" strokeWidth={2} fill="none" />
                    <Area type="monotone" dataKey="Gold" stroke="#D4AF37" strokeWidth={3} fill="url(#gGold)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Reveal>

          {/* Allocation + simulator */}
          <div className="flex flex-col gap-6">
            <Reveal delay={0.1}>
              <div className="glass-card rounded-3xl p-7">
                <h3 className="font-serif text-xl font-normal text-charcoal">Your allocation</h3>
                <div className="mt-5 space-y-4">
                  {ALLOCATION.map((a) => (
                    <div key={a.label}>
                      <div className="mb-1.5 flex justify-between text-sm">
                        <span className="text-charcoal-soft">{a.label}</span>
                        <span className="font-semibold text-charcoal">{a.pct}%</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-charcoal/5">
                        <div className="h-full rounded-full" style={{ width: `${a.pct}%`, background: a.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="glass-card rounded-3xl p-7">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-gold" />
                  <h3 className="font-serif text-xl font-normal text-charcoal">What-if simulator</h3>
                </div>
                <p className="mt-4 text-sm text-charcoal-soft">If you save this every month for 8 years:</p>
                <p className="mt-2 font-serif text-3xl text-gold-gradient">
                  ₹{projected.toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-charcoal-soft">projected gold value*</p>
                <div className="mt-5">
                  <div className="mb-2 flex justify-between text-sm font-medium text-charcoal">
                    <span>Monthly SIP</span>
                    <span>₹{monthly[0].toLocaleString("en-IN")}</span>
                  </div>
                  <Slider
                    data-testid="whatif-slider"
                    value={monthly}
                    onValueChange={setMonthly}
                    min={500}
                    max={20000}
                    step={500}
                    className="[&_[role=slider]]:border-gold [&_[role=slider]]:bg-gold"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
